// Generated by ReScript, PLEASE EDIT WITH CARE

import * as React from "react";
import Numeral from "numeral";
import * as Belt_Map from "rescript/lib/es6/belt_Map.js";
import * as Belt_Array from "rescript/lib/es6/belt_Array.js";
import * as Caml_option from "rescript/lib/es6/caml_option.js";
import * as Db$Coronate from "../Db.res.js";
import * as ReactFeather from "react-feather";
import * as Hooks$Coronate from "../Hooks.res.js";
import * as Utils$Coronate from "../Utils.res.js";
import * as Data_Id$Coronate from "../Data/Data_Id.res.js";
import * as Belt_MutableQueue from "rescript/lib/es6/belt_MutableQueue.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as Externals$Coronate from "../Externals/Externals.res.js";
import * as Data_Match$Coronate from "../Data/Data_Match.res.js";
import * as Data_Config$Coronate from "../Data/Data_Config.res.js";
import * as Data_Player$Coronate from "../Data/Data_Player.res.js";
import * as Data_Rounds$Coronate from "../Data/Data_Rounds.res.js";
import * as HelpDialogs$Coronate from "../HelpDialogs.res.js";
import * as Data_Pairing$Coronate from "../Data/Data_Pairing.res.js";
import VisuallyHidden from "@reach/visually-hidden";
import * as TournamentUtils$Coronate from "./TournamentUtils.res.js";
import * as PageTourneyPlayers$Coronate from "./PageTourneyPlayers.res.js";

function autoPair(pairData, byeValue, playerMap, byeQueue) {
  var filteredData = Data_Pairing$Coronate.keep(pairData, (function (id, param) {
          return Belt_Map.has(playerMap, id);
        }));
  var match = Data_Pairing$Coronate.setByePlayer(byeQueue, Data_Id$Coronate.dummy, filteredData);
  var byePlayerData = match[1];
  var pairs = Belt_MutableQueue.fromArray(Data_Pairing$Coronate.pairPlayers(match[0]));
  if (byePlayerData !== undefined) {
    Belt_MutableQueue.add(pairs, [
          Data_Pairing$Coronate.id(Caml_option.valFromOption(byePlayerData)),
          Data_Id$Coronate.dummy
        ]);
  }
  return Belt_MutableQueue.map(pairs, (function (param) {
                var blackId = param[1];
                var whiteId = param[0];
                var white = Data_Player$Coronate.getMaybe(playerMap, whiteId);
                var black = Data_Player$Coronate.getMaybe(playerMap, blackId);
                return {
                        id: Data_Id$Coronate.random(),
                        whiteId: whiteId,
                        blackId: blackId,
                        whiteNewRating: white.rating,
                        blackNewRating: black.rating,
                        whiteOrigRating: white.rating,
                        blackOrigRating: black.rating,
                        result: Data_Match$Coronate.Result.scoreByeMatch(whiteId, blackId, byeValue, "NotSet")
                      };
              }));
}

function numFromPair(a, b) {
  if (a !== undefined) {
    if (b !== undefined) {
      return "Two";
    } else {
      return "One";
    }
  } else if (b !== undefined) {
    return "One";
  } else {
    return "Zero";
  }
}

function validateResult(a, b, byeValue, $$default) {
  if (a !== undefined && b !== undefined) {
    return Data_Match$Coronate.Result.scoreByeMatch(Caml_option.valFromOption(a), Caml_option.valFromOption(b), byeValue, $$default);
  } else {
    return "NotSet";
  }
}

function reducer(state, action) {
  var result = state.result;
  var p2 = state.p2;
  var p1 = state.p1;
  if (typeof action !== "object") {
    switch (action) {
      case "RemoveP1" :
          return {
                  p1: undefined,
                  p2: state.p2,
                  result: "NotSet",
                  num: numFromPair(undefined, p2),
                  byeValue: state.byeValue
                };
      case "RemoveP2" :
          return {
                  p1: state.p1,
                  p2: undefined,
                  result: "NotSet",
                  num: numFromPair(p1, undefined),
                  byeValue: state.byeValue
                };
      case "Reverse" :
          return {
                  p1: p2,
                  p2: p1,
                  result: Data_Match$Coronate.Result.reverse(result),
                  num: state.num,
                  byeValue: state.byeValue
                };
      case "Clear" :
          return {
                  p1: undefined,
                  p2: undefined,
                  result: "NotSet",
                  num: "Zero",
                  byeValue: state.byeValue
                };
      
    }
  } else {
    if (action.TAG !== "Add") {
      return {
              p1: state.p1,
              p2: state.p2,
              result: action._0,
              num: state.num,
              byeValue: state.byeValue
            };
    }
    var id = action._0;
    var match = p1 !== undefined ? (
        p2 !== undefined ? [
            p1,
            p2
          ] : [
            p1,
            Caml_option.some(id)
          ]
      ) : [
        Caml_option.some(id),
        p2
      ];
    var p2$1 = match[1];
    var p1$1 = match[0];
    return {
            p1: p1$1,
            p2: p2$1,
            result: validateResult(p1$1, p2$1, state.byeValue, result),
            num: numFromPair(p1$1, p2$1),
            byeValue: state.byeValue
          };
  }
}

function useStageState(byeValue) {
  return React.useReducer(reducer, {
              p1: undefined,
              p2: undefined,
              result: "NotSet",
              num: "Zero",
              byeValue: byeValue
            });
}

var sortByFirstName = {
  TAG: "GetString",
  _0: (function (x) {
      return x.player.firstName;
    })
};

var sortByLastName = {
  TAG: "GetString",
  _0: (function (x) {
      return x.player.lastName;
    })
};

var sortByIdeal = {
  TAG: "GetFloat",
  _0: (function (x) {
      return x.ideal;
    })
};

function isPlayerSelectable(state, id) {
  var match = state.p1;
  var match$1 = state.p2;
  if (match !== undefined) {
    if (match$1 !== undefined) {
      return false;
    } else {
      return !Data_Id$Coronate.eq(Caml_option.valFromOption(match), id);
    }
  } else if (match$1 !== undefined) {
    return !Data_Id$Coronate.eq(Caml_option.valFromOption(match$1), id);
  } else {
    return true;
  }
}

function PairPicker$SelectPlayerRow(props) {
  var dispatch = props.dispatch;
  var state = props.state;
  var player = props.player;
  var match = state.num;
  var tmp;
  switch (match) {
    case "One" :
        tmp = Numeral(props.ideal).format("%");
        break;
    case "Zero" :
    case "Two" :
        tmp = "-";
        break;
    
  }
  return JsxRuntime.jsxs("tr", {
              children: [
                JsxRuntime.jsx("td", {
                      children: JsxRuntime.jsxs("button", {
                            children: [
                              JsxRuntime.jsx(ReactFeather.UserPlus, {}),
                              JsxRuntime.jsx(VisuallyHidden, {
                                    children: "Add " + Data_Player$Coronate.fullName(player)
                                  })
                            ],
                            className: "button-ghost",
                            disabled: !isPlayerSelectable(state, player.id),
                            onClick: (function (param) {
                                dispatch({
                                      TAG: "Add",
                                      _0: player.id
                                    });
                              })
                          })
                    }),
                JsxRuntime.jsx("td", {
                      children: player.firstName,
                      className: "pageround__selectlist-name"
                    }),
                JsxRuntime.jsx("td", {
                      children: player.lastName,
                      className: "pageround__selectlist-name"
                    }),
                JsxRuntime.jsx("td", {
                      children: tmp
                    })
              ],
              className: Data_Player$Coronate.Type.toString(player.type_)
            });
}

function PairPicker$SelectList(props) {
  var unmatched = props.unmatched;
  var dispatch = props.dispatch;
  var state = props.state;
  var pairData = props.pairData;
  var initialTable = Belt_Array.map(Belt_Map.valuesToArray(unmatched), (function (player) {
          return {
                  player: player,
                  ideal: 0.0
                };
        }));
  var match = Hooks$Coronate.useSortedTable(initialTable, sortByFirstName, false);
  var sortedDispatch = match[1];
  var sorted = match[0];
  React.useEffect((function () {
          var calcIdealOrNot = function (playerId) {
            var match = state.p1;
            var match$1 = state.p2;
            var selectedPlayer;
            if (match !== undefined) {
              if (match$1 !== undefined) {
                return 0;
              }
              selectedPlayer = Caml_option.valFromOption(match);
            } else {
              if (match$1 === undefined) {
                return 0;
              }
              selectedPlayer = Caml_option.valFromOption(match$1);
            }
            var ideal = Data_Pairing$Coronate.calcPairIdealByIds(pairData, selectedPlayer, playerId);
            if (ideal !== undefined) {
              return ideal / Data_Pairing$Coronate.maxPriority(pairData);
            } else {
              return 0;
            }
          };
          var table = Belt_Array.map(Belt_Map.valuesToArray(unmatched), (function (player) {
                  return {
                          player: player,
                          ideal: calcIdealOrNot(player.id)
                        };
                }));
          sortedDispatch({
                TAG: "SetTable",
                _0: table
              });
        }), [
        unmatched,
        pairData,
        sortedDispatch,
        state
      ]);
  if (Belt_Map.size(unmatched) === 0) {
    return null;
  } else {
    return JsxRuntime.jsxs("table", {
                children: [
                  JsxRuntime.jsx("thead", {
                        children: JsxRuntime.jsxs("tr", {
                              children: [
                                JsxRuntime.jsx("th", {
                                      children: JsxRuntime.jsx(VisuallyHidden, {
                                            children: "Controls"
                                          })
                                    }),
                                JsxRuntime.jsx("th", {
                                      children: JsxRuntime.jsx(Hooks$Coronate.SortButton.make, {
                                            children: "First name",
                                            sortColumn: sortByFirstName,
                                            data: sorted,
                                            dispatch: sortedDispatch
                                          })
                                    }),
                                JsxRuntime.jsx("th", {
                                      children: JsxRuntime.jsx(Hooks$Coronate.SortButton.make, {
                                            children: "Last name",
                                            sortColumn: sortByLastName,
                                            data: sorted,
                                            dispatch: sortedDispatch
                                          })
                                    }),
                                JsxRuntime.jsx("th", {
                                      children: JsxRuntime.jsx(Hooks$Coronate.SortButton.make, {
                                            children: "Ideal",
                                            sortColumn: sortByIdeal,
                                            data: sorted,
                                            dispatch: sortedDispatch
                                          })
                                    })
                              ]
                            })
                      }),
                  JsxRuntime.jsxs("tbody", {
                        children: [
                          Belt_Array.map(sorted.table, (function (param) {
                                  var player = param.player;
                                  return JsxRuntime.jsx(PairPicker$SelectPlayerRow, {
                                              player: player,
                                              ideal: param.ideal,
                                              state: state,
                                              dispatch: dispatch
                                            }, Data_Id$Coronate.toString(player.id));
                                })),
                          JsxRuntime.jsx(PairPicker$SelectPlayerRow, {
                                player: Data_Player$Coronate.dummy,
                                ideal: 0.0,
                                state: state,
                                dispatch: dispatch
                              })
                        ]
                      })
                ],
                className: "content pageround__select-list"
              });
  }
}

function PairPicker$Stage(props) {
  var tourney = props.tourney;
  var roundList = tourney.roundList;
  var config = props.config;
  var round = props.round;
  var setTourney = props.setTourney;
  var dispatch = props.dispatch;
  var state = props.state;
  var roundId = props.roundId;
  var pairData = props.pairData;
  var getPlayer = props.getPlayer;
  var player = state.p1;
  var whiteName = player !== undefined ? Data_Player$Coronate.fullName(getPlayer(Caml_option.valFromOption(player))) : "";
  var player$1 = state.p2;
  var blackName = player$1 !== undefined ? Data_Player$Coronate.fullName(getPlayer(Caml_option.valFromOption(player$1))) : "";
  var match = function (param) {
    var match$1 = state.p1;
    var match$2 = state.p2;
    if (match$1 === undefined) {
      return ;
    }
    if (match$2 === undefined) {
      return ;
    }
    var newRound = Data_Rounds$Coronate.Round.addMatches(round, [Data_Match$Coronate.manualPair(getPlayer(Caml_option.valFromOption(match$1)), getPlayer(Caml_option.valFromOption(match$2)), state.result, config.byeValue)]);
    var roundList$1 = Data_Rounds$Coronate.set(roundList, roundId, newRound);
    if (roundList$1 !== undefined) {
      setTourney({
            id: tourney.id,
            name: tourney.name,
            date: tourney.date,
            playerIds: tourney.playerIds,
            scoreAdjustments: tourney.scoreAdjustments,
            byeQueue: tourney.byeQueue,
            tieBreaks: tourney.tieBreaks,
            roundList: Caml_option.valFromOption(roundList$1)
          });
    } else {
      console.error("Couldn't add round " + String(roundId));
    }
    dispatch("Clear");
  };
  var match$1 = state.p1;
  var match$2 = state.p2;
  var matchIdeal;
  if (match$1 !== undefined && match$2 !== undefined) {
    var ideal = Data_Pairing$Coronate.calcPairIdealByIds(pairData, Caml_option.valFromOption(match$1), Caml_option.valFromOption(match$2));
    matchIdeal = ideal !== undefined ? Numeral(ideal / Data_Pairing$Coronate.maxPriority(pairData)).format("%") : null;
  } else {
    matchIdeal = null;
  }
  var p = state.p1;
  var p$1 = state.p2;
  return JsxRuntime.jsxs("div", {
              children: [
                JsxRuntime.jsx("h2", {
                      children: "Selected for matching:"
                    }),
                JsxRuntime.jsxs("div", {
                      children: [
                        JsxRuntime.jsxs("p", {
                              children: [
                                Data_Config$Coronate.aliasToStringWhite(config),
                                ": ",
                                p !== undefined ? JsxRuntime.jsxs(JsxRuntime.Fragment, {
                                        children: [
                                          JsxRuntime.jsx("span", {
                                                children: whiteName + " ",
                                                className: Data_Player$Coronate.Type.toString(getPlayer(Caml_option.valFromOption(p)).type_)
                                              }),
                                          JsxRuntime.jsxs("button", {
                                                children: [
                                                  JsxRuntime.jsx(ReactFeather.UserMinus, {}),
                                                  JsxRuntime.jsx(VisuallyHidden, {
                                                        children: " Remove " + whiteName
                                                      })
                                                ],
                                                className: "button-micro button-ghost",
                                                onClick: (function (param) {
                                                    dispatch("RemoveP1");
                                                  })
                                              })
                                        ]
                                      }) : null
                              ]
                            }),
                        JsxRuntime.jsxs("p", {
                              children: [
                                Data_Config$Coronate.aliasToStringBlack(config),
                                ": ",
                                p$1 !== undefined ? JsxRuntime.jsxs(JsxRuntime.Fragment, {
                                        children: [
                                          JsxRuntime.jsx("span", {
                                                children: blackName + " ",
                                                className: Data_Player$Coronate.Type.toString(getPlayer(Caml_option.valFromOption(p$1)).type_)
                                              }),
                                          JsxRuntime.jsxs("button", {
                                                children: [
                                                  JsxRuntime.jsx(ReactFeather.UserMinus, {}),
                                                  JsxRuntime.jsx(VisuallyHidden, {
                                                        children: " Remove " + blackName
                                                      })
                                                ],
                                                className: "button-micro button-ghost",
                                                onClick: (function (param) {
                                                    dispatch("RemoveP2");
                                                  })
                                              })
                                        ]
                                      }) : null
                              ]
                            }),
                        JsxRuntime.jsxs("p", {
                              children: [
                                "Match ideal: ",
                                matchIdeal
                              ]
                            })
                      ],
                      className: "content"
                    }),
                JsxRuntime.jsx("p", {
                      children: JsxRuntime.jsxs("label", {
                            children: [
                              "Pre-select winner ",
                              JsxRuntime.jsx(Utils$Coronate.TestId.make, {
                                    children: JsxRuntime.jsxs("select", {
                                          children: [
                                            JsxRuntime.jsx("option", {
                                                  children: "None",
                                                  value: Data_Match$Coronate.Result.toString("NotSet")
                                                }),
                                            JsxRuntime.jsxs("option", {
                                                  children: [
                                                    Data_Config$Coronate.aliasToStringWhite(config),
                                                    " won"
                                                  ],
                                                  value: Data_Match$Coronate.Result.toString("WhiteWon")
                                                }),
                                            JsxRuntime.jsxs("option", {
                                                  children: [
                                                    Data_Config$Coronate.aliasToStringBlack(config),
                                                    " won"
                                                  ],
                                                  value: Data_Match$Coronate.Result.toString("BlackWon")
                                                }),
                                            JsxRuntime.jsx("option", {
                                                  children: "Draw",
                                                  value: Data_Match$Coronate.Result.toString("Draw")
                                                }),
                                            JsxRuntime.jsx("option", {
                                                  children: "Aborted",
                                                  value: Data_Match$Coronate.Result.toString("Aborted")
                                                }),
                                            JsxRuntime.jsxs("option", {
                                                  children: [
                                                    Data_Config$Coronate.aliasToStringWhite(config),
                                                    " Aborted"
                                                  ],
                                                  value: Data_Match$Coronate.Result.toString("WhiteAborted")
                                                }),
                                            JsxRuntime.jsxs("option", {
                                                  children: [
                                                    Data_Config$Coronate.aliasToStringBlack(config),
                                                    " Aborted"
                                                  ],
                                                  value: Data_Match$Coronate.Result.toString("BlackAborted")
                                                })
                                          ],
                                          disabled: state.num !== "Two",
                                          value: Data_Match$Coronate.Result.toString(state.result),
                                          onBlur: (function (e) {
                                              dispatch({
                                                    TAG: "SetResult",
                                                    _0: Data_Match$Coronate.Result.fromString(e.target.value)
                                                  });
                                            }),
                                          onChange: (function (e) {
                                              dispatch({
                                                    TAG: "SetResult",
                                                    _0: Data_Match$Coronate.Result.fromString(e.target.value)
                                                  });
                                            })
                                        }),
                                    testId: "pairpicker-preselect-winner"
                                  })
                            ]
                          })
                    }),
                JsxRuntime.jsxs("div", {
                      children: [
                        JsxRuntime.jsxs("button", {
                              children: [
                                JsxRuntime.jsx(ReactFeather.Repeat, {}),
                                " Swap colors"
                              ],
                              disabled: state.num === "Zero",
                              onClick: (function (param) {
                                  dispatch("Reverse");
                                })
                            }),
                        " ",
                        JsxRuntime.jsxs("button", {
                              children: [
                                JsxRuntime.jsx(ReactFeather.Check, {}),
                                " Match selected"
                              ],
                              className: "button-primary",
                              disabled: state.num !== "Two",
                              onClick: match
                            })
                      ],
                      className: "toolbar"
                    })
              ]
            });
}

function PairPicker$PlayerInfo(props) {
  var match = TournamentUtils$Coronate.getScoreInfo(props.player, props.scoreData, props.config, props.getPlayer, props.players, props.origRating, props.newRating);
  var player = match.player;
  return JsxRuntime.jsxs("div", {
              children: [
                JsxRuntime.jsx("h3", {
                      children: Data_Player$Coronate.fullName(player)
                    }),
                JsxRuntime.jsxs("dl", {
                      children: [
                        JsxRuntime.jsx("dt", {
                              children: "Score"
                            }),
                        JsxRuntime.jsx("dd", {
                              children: match.score
                            }),
                        JsxRuntime.jsx("dt", {
                              id: "rating-" + Data_Id$Coronate.toString(player.id)
                            }),
                        JsxRuntime.jsx("dt", {
                              children: "Rating"
                            }),
                        JsxRuntime.jsx("dd", {
                              children: match.rating
                            }),
                        JsxRuntime.jsx("dt", {
                              children: "Color balance"
                            }),
                        JsxRuntime.jsx("dd", {
                              children: match.colorBalance
                            }),
                        JsxRuntime.jsx("dt", {
                              children: "Has had a bye round"
                            }),
                        JsxRuntime.jsx("dd", {
                              children: match.hasBye ? "Yes" : "No"
                            }),
                        JsxRuntime.jsx("dt", {
                              children: "Opponent history"
                            }),
                        JsxRuntime.jsx("dd", {
                              children: JsxRuntime.jsx("ol", {
                                    children: match.opponentResults
                                  }),
                              style: {
                                margin: "0"
                              }
                            }),
                        JsxRuntime.jsx("dt", {
                              children: "Players to avoid"
                            }),
                        JsxRuntime.jsx("dd", {
                              children: JsxRuntime.jsx("ul", {
                                    children: match.avoidListHtml
                                  }),
                              style: {
                                margin: "0"
                              }
                            })
                      ]
                    })
              ],
              className: "player-card"
            });
}

function PairPicker(props) {
  var unmatchedWithDummy = props.unmatchedWithDummy;
  var unmatched = props.unmatched;
  var scoreData = props.scoreData;
  var tournament = props.tournament;
  var roundId = props.roundId;
  var match = Db$Coronate.useConfig();
  var config = match[0];
  var match$1 = useStageState(config.byeValue);
  var tourney = tournament.tourney;
  var roundList = tourney.roundList;
  var byeQueue = tourney.byeQueue;
  var setTourney = tournament.setTourney;
  var players = tournament.players;
  var getPlayer = tournament.getPlayer;
  var activePlayers = tournament.activePlayers;
  var dispatch = match$1[1];
  var state = match$1[0];
  var round = Data_Rounds$Coronate.get(roundList, roundId);
  var addOrRemovePlayers = Hooks$Coronate.useBool(false);
  var autoPairHelp = Hooks$Coronate.useBool(false);
  var pairData = React.useMemo((function () {
          return Data_Pairing$Coronate.make(scoreData, activePlayers, config.avoidPairs);
        }), [
        activePlayers,
        config.avoidPairs,
        scoreData
      ]);
  React.useEffect((function () {
          var p1 = state.p1;
          if (p1 !== undefined) {
            var match = Belt_Map.get(unmatchedWithDummy, Caml_option.valFromOption(p1));
            if (match !== undefined) {
              
            } else {
              dispatch("RemoveP1");
            }
          }
          var p2 = state.p2;
          if (p2 !== undefined) {
            var match$1 = Belt_Map.get(unmatchedWithDummy, Caml_option.valFromOption(p2));
            if (match$1 !== undefined) {
              
            } else {
              dispatch("RemoveP2");
            }
          }
          
        }), [
        unmatchedWithDummy,
        state,
        dispatch
      ]);
  if (round === undefined) {
    return JsxRuntime.jsx("div", {
                children: "No round available."
              });
  }
  var round$1 = Caml_option.valFromOption(round);
  return JsxRuntime.jsxs("div", {
              children: [
                JsxRuntime.jsxs(Utils$Coronate.PanelContainer.make, {
                      children: [
                        JsxRuntime.jsxs(Utils$Coronate.Panel.make, {
                              children: [
                                JsxRuntime.jsxs("div", {
                                      children: [
                                        JsxRuntime.jsx("button", {
                                              children: "Auto-pair unmatched players",
                                              className: "button-primary",
                                              disabled: Belt_Map.size(unmatched) === 0,
                                              onClick: (function (param) {
                                                  var newRound = Data_Rounds$Coronate.Round.addMatches(round$1, Belt_MutableQueue.toArray(autoPair(pairData, config.byeValue, unmatched, byeQueue)));
                                                  var roundList$1 = Data_Rounds$Coronate.set(roundList, roundId, newRound);
                                                  if (roundList$1 !== undefined) {
                                                    return setTourney({
                                                                id: tourney.id,
                                                                name: tourney.name,
                                                                date: tourney.date,
                                                                playerIds: tourney.playerIds,
                                                                scoreAdjustments: tourney.scoreAdjustments,
                                                                byeQueue: tourney.byeQueue,
                                                                tieBreaks: tourney.tieBreaks,
                                                                roundList: Caml_option.valFromOption(roundList$1)
                                                              });
                                                  }
                                                  
                                                })
                                            }),
                                        JsxRuntime.jsxs("button", {
                                              children: [
                                                JsxRuntime.jsx(ReactFeather.HelpCircle, {}),
                                                JsxRuntime.jsx(VisuallyHidden, {
                                                      children: "Auto-pair information."
                                                    })
                                              ],
                                              className: "button-ghost",
                                              onClick: (function (param) {
                                                  autoPairHelp.setTrue();
                                                })
                                            })
                                      ],
                                      className: "toolbar"
                                    }),
                                JsxRuntime.jsx(PairPicker$SelectList, {
                                      pairData: pairData,
                                      state: state,
                                      dispatch: dispatch,
                                      unmatched: unmatched
                                    }),
                                JsxRuntime.jsx("div", {
                                      children: JsxRuntime.jsx("button", {
                                            children: "Add or remove players from the roster.",
                                            onClick: (function (param) {
                                                addOrRemovePlayers.setTrue();
                                              })
                                          }),
                                      className: "toolbar"
                                    })
                              ]
                            }),
                        JsxRuntime.jsxs(Utils$Coronate.Panel.make, {
                              children: [
                                JsxRuntime.jsx(PairPicker$Stage, {
                                      getPlayer: getPlayer,
                                      pairData: pairData,
                                      roundId: roundId,
                                      state: state,
                                      dispatch: dispatch,
                                      setTourney: setTourney,
                                      tourney: tourney,
                                      round: round$1,
                                      config: config
                                    }),
                                JsxRuntime.jsx(Utils$Coronate.PanelContainer.make, {
                                      children: Belt_Array.map([
                                            state.p1,
                                            state.p2
                                          ], (function (id) {
                                              if (id === undefined) {
                                                return null;
                                              }
                                              var playerId = Caml_option.valFromOption(id);
                                              return JsxRuntime.jsx(Utils$Coronate.Panel.make, {
                                                          children: JsxRuntime.jsx(PairPicker$PlayerInfo, {
                                                                player: getPlayer(playerId),
                                                                scoreData: scoreData,
                                                                players: players,
                                                                config: config,
                                                                origRating: getPlayer(playerId).rating,
                                                                newRating: undefined,
                                                                getPlayer: getPlayer
                                                              })
                                                        }, Data_Id$Coronate.toString(playerId));
                                            }))
                                    })
                              ],
                              style: {
                                flexGrow: "1"
                              }
                            })
                      ]
                    }),
                JsxRuntime.jsxs(Externals$Coronate.Dialog.make, {
                      isOpen: addOrRemovePlayers.state,
                      onDismiss: addOrRemovePlayers.setFalse,
                      ariaLabel: "Select players",
                      children: [
                        JsxRuntime.jsx("button", {
                              children: "Done",
                              className: "button-micro",
                              onClick: (function (param) {
                                  addOrRemovePlayers.setFalse();
                                })
                            }),
                        JsxRuntime.jsx(PageTourneyPlayers$Coronate.Selecting.make, {
                              tourney: tourney,
                              setTourney: setTourney,
                              players: players,
                              playersDispatch: tournament.playersDispatch
                            })
                      ],
                      className: ""
                    }),
                JsxRuntime.jsx(HelpDialogs$Coronate.Pairing.make, {
                      state: autoPairHelp,
                      config: config,
                      ariaLabel: "Auto-pair information"
                    })
              ],
              className: "content-area"
            });
}

var make = PairPicker;

export {
  make ,
}
/* react Not a pure module */
